import React, { useState, useMemo } from 'react';
import Select from 'react-select';
import { Loading } from "../../../common/Loading";
import { Error } from "../../../common/Error";
import { useJiraData } from "../../../utils/useJiraData";
import { JiraAllIssuesList } from "./JiraAllIssuesList";
import { JiraTasksWrapper, FiltersContainer, FilterGroup, CheckboxGroup } from "./styled";
import { DateSelectorPerDay } from "./DateSelectorPerDay";
import { formatDate } from '../../../utils/timeCalculations';

export const JiraAllIssues = () => {
  const acceptedTasks = true;

  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const threeYearsAgo = new Date(today);
    threeYearsAgo.setFullYear(today.getFullYear() - 3);
    threeYearsAgo.setHours(0, 0, 0, 0);
    return threeYearsAgo;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [issueKey, setIssueKey] = useState(""); 

  const [showTasksWithTime, setShowTasksWithTime] = useState(false);
  const [showTasksWithEstimatedTime, setShowTasksWithEstimatedTime] = useState(false);
  const [showTasksWithRemainingTime, setShowTasksWithRemainingTime] = useState(false);

  const formattedStartDate = useMemo(() => formatDate(startDate), [startDate]);
  const formattedEndDate = useMemo(() => formatDate(endDate), [endDate]);

  const { loading, error, data } = useJiraData(acceptedTasks, formattedStartDate, formattedEndDate);

  const { statusOptions, assigneeOptions } = useMemo(() => {
    const statuses = new Set();
    const assignees = new Set();

    data?.issues.forEach((issue) => {
      const status = issue.fields.status?.name;
      const assignee = issue.fields.assignee?.displayName;

      if (status) statuses.add(status);
      if (assignee) assignees.add(assignee);
    });

    return {
      statusOptions: Array.from(statuses).map((s) => ({ label: s, value: s })),
      assigneeOptions: Array.from(assignees).map((a) => ({ label: a, value: a })),
    };
  }, [data]);

  const filteredIssues = useMemo(() => {
    if (!data) return [];

    const selectedStatusValues = selectedStatuses.map((s) => s.value);
    const selectedAssigneeValues = selectedAssignees.map((a) => a.value);

    return data.issues.filter((issue) => {
      const status = issue.fields.status?.name || '';
      const assignee = issue.fields.assignee?.displayName || '';
      const timetracking = issue.fields.timetracking || {};
      const issueId = issue.key || ""; 

      const timeSpent = timetracking.timeSpent;
      const estimatedTime = timetracking.originalEstimate;
      const remainingTime = timetracking.remainingEstimate;

      const matchesStatus =
        selectedStatusValues.length === 0 || selectedStatusValues.includes(status);
      const matchesAssignee =
        selectedAssigneeValues.length === 0 || selectedAssigneeValues.includes(assignee);

      const matchesTime =
        !showTasksWithTime || (timeSpent && timeSpent.trim() !== '');
      const matchesEstimatedTime =
        !showTasksWithEstimatedTime || (estimatedTime && estimatedTime.trim() !== '');
      const matchesRemainingTime =
        !showTasksWithRemainingTime || (remainingTime && parseFloat(remainingTime) > 0);
      const matchesIssueKey =
        !issueKey || issueId.toLowerCase().includes(issueKey.toLowerCase()); 

      return (
        matchesStatus &&
        matchesAssignee &&
        matchesTime &&
        matchesEstimatedTime &&
        matchesRemainingTime &&
        matchesIssueKey
      );
    });
  }, [
    data,
    selectedStatuses,
    selectedAssignees,
    showTasksWithTime,
    showTasksWithEstimatedTime,
    showTasksWithRemainingTime,
    issueKey, 
  ]);

  if (loading) return <Loading />;
  if (error) return <Error message={error} />;
  if (!data) return <p>Brak wyników dla bieżących ustawień!</p>;

  return (
    <JiraTasksWrapper>
      <DateSelectorPerDay
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
      />

      <FiltersContainer>
        <FilterGroup>
          <label>Status:</label>
          <Select
            isMulti
            options={statusOptions}
            value={selectedStatuses}
            onChange={setSelectedStatuses}
            placeholder="Filtruj po statusie..."
          />
        </FilterGroup>

        <FilterGroup>
          <label>Osoba przypisana:</label>
          <Select
            isMulti
            options={assigneeOptions}
            value={selectedAssignees}
            onChange={setSelectedAssignees}
            placeholder="Filtruj po osobie przypisanej..."
          />
        </FilterGroup>

        <FilterGroup>
          <label>Klucz zgłoszenia:</label>
          <input
            type="text"
            value={issueKey}
            onChange={(e) => setIssueKey(e.target.value)}
            placeholder="Wpisz klucz zgłoszenia..."
          />
        </FilterGroup>
  

      <CheckboxGroup>
        <label>
          <input
            type="checkbox"
            checked={showTasksWithTime}
            onChange={(e) => setShowTasksWithTime(e.target.checked)}
          />
          Pokaż zadania z czasem zarejestrowanym
        </label>

        <label>
          <input
            type="checkbox"
            checked={showTasksWithEstimatedTime}
            onChange={(e) => setShowTasksWithEstimatedTime(e.target.checked)}
          />
          Pokaż zadania z szacowanym czasem
        </label>

        <label>
          <input
            type="checkbox"
            checked={showTasksWithRemainingTime}
            onChange={(e) => setShowTasksWithRemainingTime(e.target.checked)}
          />
          Pokaż zadania z czasem pozostałym do zarejestrowania
        </label>
      </CheckboxGroup>
      </FiltersContainer>

      <JiraAllIssuesList data={{ issues: filteredIssues }} showAllTasks />
    </JiraTasksWrapper>
  );
};
